import { bind, forEach } from 'lodash';
import { action, observable } from 'mobx';
import { HttpClient, setMap } from 'Mri.Web.Common';

export class PartsStore {
    public constructor(httpClient: HttpClient) {
        this.httpClient = httpClient;
    }

    @action
    public async loadAllForPartDefinitionAsync(partDefinitionGuid: string) {
        const parts = await this.httpClient.get<Mri.Parts.Part[]>(
            `/api/v1/parts?partDefinitionGuid=${partDefinitionGuid}`
        );

        setMap(this.parts, parts, m => m.partGuid);
        parts?.forEach(p => this.collatePart(p));
    }

    @action
    public async createAsync(model: Mri.Parts.Part) {
        const part = await this.httpClient.post<Mri.Parts.Part>(`/api/v1/parts`, model);

        if (part) {
            this.parts.set(part.partGuid, part);
            this.impoundChildren(part);
        }
    }

    @action
    public async loadPartsFromGuid(partGuid: string, includeChildren?: boolean) {
        const part = await this.httpClient.get<Mri.Parts.Part>(
            `/api/v1/parts/${partGuid}?includeChildren=${includeChildren ?? false}`
        );

        if (part) {
            this.parts.set(part.partGuid, part);
            this.impoundChildren(part);
        }
    }

    @action
    public async addPartAsync(model: Mri.Parts.Part, partGuid: string) {
        const part = await this.httpClient.put<Mri.Parts.Part>(`/api/v1/parts/${partGuid}`, model);

        if (part) {
            this.collatePart(part);
        }
    }

    @action
    public async removePartAsync(model: Mri.Parts.Part, partGuid: string) {
        model.parentPartGuid = undefined;

        const part = await this.httpClient.put<Mri.Parts.Part>(`/api/v1/parts/${partGuid}`, model);

        this.collatePart(part);
    }

    @action
    public async updateStatusAsync(model: Mri.PartStatus.PartStatus, partGuid: string) {
        const part = await this.httpClient.put<Mri.Parts.Part>(`/api/v1/parts/${partGuid}/status`, model);

        this.collatePart(part);
    }

    @action
    public async loadApplicableChildrenAsync(partGuid: string) {
        const parts = await this.httpClient.get<Mri.Parts.Part[]>(`/api/v1/parts/${partGuid}/children/part`);

        if (parts) this.applicableChildParts.set(partGuid, parts);
    }

    @action
    public async loadApplicableChildDefsAsync(partGuid: string) {
        const partDefinitions = await this.httpClient.get<Mri.Parts.PartDefinition[]>(
            `/api/v1/parts/${partGuid}/children/part-definitions`
        );

        if (partDefinitions) this.applicableChildDefinitions.set(partGuid, partDefinitions);
    }

    @action
    public async loadStatusLogAsync(partGuid: string, includeChildren?: boolean) {
        const statusLog = await this.httpClient.get<Mri.PartStatus.PartStatusLog[]>(
            `/api/v1/parts/${partGuid}/status-log?includeChildren=${includeChildren ?? false}`
        );
        if (statusLog) {
            this.statusLogs = statusLog.reduce((agg, l) => {
                const log = agg.has(l.partGuid) ? agg.get(l.partGuid) ?? [] : [];
                log.push(l.partStatus);
                agg.set(l.partGuid, log);
                return agg;
            }, new Map<string, Mri.PartStatus.PartStatus[]>());
        }
    }

    @action
    public async loadPartTagDataAsync(partGuid: string) {
        const tagDatas = await this.httpClient.get<Mri.PartTagData.PartTagDatum[]>(
            `/api/v1/parts/${partGuid}/tag-data`
        );
        if (tagDatas) this.partTagDatas.set(partGuid, tagDatas);
    }

    public getPartStatusLogsForTree(partGuid: string) {
        if (!this.parts.has(partGuid)) return [];

        const partLogs: { part: Mri.Parts.Part; status: Mri.PartStatus.PartStatus }[] = [];
        const part = this.parts.get(partGuid);
        const logs = this.statusLogs.get(partGuid);

        if (part && logs) {
            for (const status of logs) {
                partLogs.push({ part, status });
            }
        }

        if (part?.children) {
            for (const child of part.children) {
                const childLogs = this.getPartStatusLogsForTree(child.partGuid);

                for (const childLog of childLogs) {
                    partLogs.push(childLog);
                }
            }
        }

        return partLogs;
    }

    private collatePart(part: Mri.Parts.Part | undefined) {
        if (part) {
            this.parts.set(part.partGuid, part);
            this.impoundChildren(part);

            if (this.statusLogs.has(part.partGuid)) {
                const log = this.statusLogs.get(part.partGuid);
                if (log) log.push(part.partStatus);
            }
        }
    }

    private impoundChildren(part: Mri.Parts.Part) {
        if (!part.children) return;
        const impoundedChildren: Mri.Parts.Part[] = []; // TRUMP DID IT

        for (const child of part.children) {
            this.impoundChildren(child);

            this.parts.set(child.partGuid, child);
            impoundedChildren.push(this.parts.get(child.partGuid)!);
        }

        part.children = impoundedChildren;
    }

    @observable public statusLogs = new Map<string, Mri.PartStatus.PartStatus[]>();
    @observable public parts = new Map<string, Mri.Parts.Part>();
    @observable public applicableChildParts = new Map<string, Mri.Parts.Part[]>();
    @observable public applicableChildDefinitions = new Map<string, Mri.Parts.PartDefinition[]>();
    @observable public partTagDatas = new Map<string, Mri.PartTagData.PartTagDatum[]>();

    private readonly httpClient: HttpClient;
}
